import React, { useState, useEffect, useRef } from "react";
import "./Footer.css";
import useTranslations from "../../../Hooks/useTranslations";
import translations from "./translations.json";

const Footer = ({ isSidebarExpanded = false }) => {
  const t = useTranslations(translations);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const footerRef = useRef(null);

  // Donation goal settings
  const currentAmount = 770;
  const goalAmount = 3000;
  const targetProgress = (currentAmount / goalAmount) * 100;
  const animationDuration = 2500; // 2.5 seconds for a smooth effect

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // Trigger only if visible and hasn't animated yet
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 } // Trigger when 30% of the footer is visible
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) observer.unobserve(footerRef.current);
    };
  }, [hasAnimated]);

  useEffect(() => {
    if (!hasAnimated) return;

    let startTime = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / animationDuration, 1);

      // Easing function: easeOutQuart (1 - (1 - x)^4) for an elegant slow-down
      const easeOut = 1 - Math.pow(1 - progress, 4);

      setAnimatedProgress(easeOut * targetProgress);

      if (progress < 1) {
        window.requestAnimationFrame(animate);
      }
    };

    window.requestAnimationFrame(animate);
  }, [hasAnimated, targetProgress]);

  return (
    <div
      className={`footer-component ${isSidebarExpanded ? "sidebar-expanded" : ""}`}
      ref={footerRef}
    >
      <div className="footer-wave">
        <svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path fill="#7DD3C0" fillOpacity="0.7" d="M0,96L120,112C240,128,480,160,720,160C960,160,1200,128,1320,112L1440,96L1440,320L0,320Z"></path>
          <path fill="#5BC0BE" fillOpacity="0.8" d="M0,128L120,138.7C240,149,480,171,720,165.3C960,160,1200,128,1320,112L1440,96L1440,320L0,320Z"></path>
          <path fill="#3A9D9A" fillOpacity="0.9" d="M0,160L120,170.7C240,181,480,203,720,197.3C960,192,1200,160,1320,144L1440,128L1440,320L0,320Z"></path>
          <path fill="#055b69" fillOpacity="1" d="M0,192L120,208C240,224,480,256,720,261.3C960,267,1200,245,1320,234.7L1440,224L1440,320L0,320Z"></path>
        </svg>
      </div>
      <footer className="app-footer">
        <div className="footer-content">
          {/* Left column: Links */}
          <div className="footer-links-column">
            <h4 className="footer-section-title">{t.linksTitle}</h4>
            <a
              href="https://happychef.cloud/horeca-website.html"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              {t.horecaWebsite}
            </a>
            <a
              href="https://feedback.happychef.cloud/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              {t.sendFeedback}
            </a>
            <div className="footer-app-badges">
              <a
                href="https://apps.apple.com/be/app/happychef-app/id6740510109"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-app-badge-link"
              >
                <img
                  src="https://static.reservaties.net/images/happychef-cloud/app-store/apple.svg"
                  alt={t.appStoreAlt}
                  className="footer-app-badge"
                  width="135"
                  height="40"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.happychef.app"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-app-badge-link"
              >
                <img
                  src="https://static.reservaties.net/images/happychef-cloud/app-store/google.png"
                  alt={t.playStoreAlt}
                  className="footer-app-badge"
                  width="151"
                  height="45"
                />
              </a>
            </div>
          </div>

          {/* Middle column: Gratis Cursussen */}
          <div className="footer-courses-column">
            <h4 className="footer-courses-title">{t.freeCourses}</h4>
            <a
              href="https://academy.happychef.cloud/course/reservatiesysteem-basics-training"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              {t.basicTraining}
            </a>
            <a
              href="https://academy.happychef.cloud/course/het-1-geheim-om-no-shows-in-te-vullen"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
            >
              {t.lastMinutes}
            </a>
          </div>

          {/* Right column: Alzheimer Goal */}
          <div className="footer-alzheimer-column">
            <div className="footer-goal-row">
              <a
                href="https://happychef.cloud/onze-missie.html"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-logo-link"
              >
                <img
                  src="https://static.reservaties.net/images/charity/alzheimer-logo-white.webp"
                  alt={t.logoAlt}
                  className="footer-logo"
                  width="100"
                  height="100"
                />
              </a>
              <div className="footer-goal-container">
                <div className="footer-goal-header">
                  <span className="goal-label">{t.goalLabel}</span>
                  <span className="goal-amounts">
                    €{currentAmount} / €{goalAmount}
                  </span>
                </div>
                <div className="footer-progress-bar">
                  <div
                    className="footer-progress-fill"
                    style={{ width: `${animatedProgress}%` }}
                  />
                </div>
                <div className="goal-deadline">{t.goalDeadline}</div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;